import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { CgtFieldType, FieldDefinition } from '@/types/cgt'
import type { LibraryFieldImportRow } from '@/lib/formExcel'

/**
 * Field props accepted when creating a library field (no id/order — backend owns
 * those).
 *
 * `systemName` is omitted too, for a different reason: it is curated directly in
 * the database, so the backend DTOs do not declare it and its
 * `forbidNonWhitelisted` ValidationPipe would 400 a payload carrying it.
 */
export type CreateLibraryFieldPayload = Omit<
  FieldDefinition,
  'id' | 'order' | 'libraryFieldId' | 'systemName'
>

/** Partial props for updating a library field (PUT, not PATCH). */
export type UpdateLibraryFieldPayload = Partial<CreateLibraryFieldPayload>

/** A form that has copied a given library field, as returned by the usage endpoint. */
export interface FieldUsageForm {
  id: string
  name: string
  version: number
  category: string
}

/** Response of `GET /field-library/:id/usage`. */
export interface FieldUsage {
  fieldId: string
  count: number
  forms: FieldUsageForm[]
}

/** Query params accepted by `GET /field-library`. */
export interface FieldLibraryListParams {
  q?: string
  fieldType?: CgtFieldType
  categoryId?: string
  /** Omit for all rows, true for enabled-only, false for disabled-only. */
  enabled?: boolean
  /**
   * Pass `false` to leave out rows the e2e suite created.
   *
   * Worth passing on any list a real user reads. The suite mints a library row
   * for every Process field it places on a canvas and then deletes the form, so
   * the rows pile up — uniquely named, in no form, never run — and used to land
   * in front of users as "Not in any form" clutter. An e2e run still sees its
   * own rows: the harness stamps `X-E2E-Test` on every request and the server
   * drops this filter when it sees it.
   */
  isTestData?: boolean
  /**
   * Narrow to fields USED BY a process step. A step is a Process field, keyed
   * by its libraryFieldId (or its own id when it was added on the canvas); a
   * library field is used by it when a copy sits on an active form that also
   * holds that Process field. Keys come from `listProcessSteps`.
   */
  processStepId?: string
  skip?: number
  take?: number
  /** Column sort, `<column>:<asc|desc>`, applied server-side before paging (z8nrz7e4h6). */
  sort?: string
}

/**
 * Why a row matched `processStepId`. Only `used` exists today. An explicit
 * "intended for this step" association is not modelled yet; when it is,
 * `intended` and `both` join this union and the badge reads the value.
 */
export type StepMatch = 'used'

/** A list row: the field, plus `stepMatch` only while a step filter is active. */
export type FieldLibraryListItem = FieldDefinition & { stepMatch?: StepMatch }

/** Response of `GET /field-library` (was a bare array — now a paginated object). */
export interface FieldLibraryListResult {
  items: FieldLibraryListItem[]
  total: number
  /** Counts per type for the current search (ignores the selected fieldType filter). */
  typeCounts: Record<string, number>
}

/**
 * One process step, as `GET /field-library/process-steps` names it: one entry
 * per step key, named by the newest active form that holds it, with how many
 * active forms hold it.
 */
export interface ProcessStepSummary {
  fieldId: string
  label: string
  formName: string
  formVersion: number
  formCount: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const fieldLibraryApi = {
  listFields: (params: FieldLibraryListParams = {}) => {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.fieldType) qs.set('fieldType', params.fieldType)
    if (params.categoryId) qs.set('categoryId', params.categoryId)
    if (params.enabled != null) qs.set('enabled', String(params.enabled))
    if (params.isTestData != null) qs.set('isTestData', String(params.isTestData))
    if (params.processStepId) qs.set('processStepId', params.processStepId)
    if (params.skip != null) qs.set('skip', String(params.skip))
    if (params.take != null) qs.set('take', String(params.take))
    if (params.sort) qs.set('sort', params.sort)
    const query = qs.toString()
    return request<FieldLibraryListResult>(`/field-library${query ? `?${query}` : ''}`)
  },

  /**
   * Fetch every library field by paging through the list endpoint (which caps
   * `take` at 100). Used by the Excel template/import flow, which needs the full
   * set to build dropdowns and resolve field names — the paginated picker query
   * is not enough.
   */
  listAllFields: async (): Promise<FieldDefinition[]> => {
    const PAGE = 100
    const all: FieldDefinition[] = []
    for (let skip = 0; ; skip += PAGE) {
      const { items, total } = await fieldLibraryApi.listFields({ skip, take: PAGE })
      all.push(...items)
      if (all.length >= total || items.length === 0) break
    }
    return all
  },

  getField: (id: string) => request<FieldDefinition>(`/field-library/${id}`),

  getUsage: (id: string) => request<FieldUsage>(`/field-library/${id}/usage`),

  /** The tenant's process steps, for the picker's step filter. */
  listProcessSteps: () => request<ProcessStepSummary[]>('/field-library/process-steps'),

  createField: (payload: CreateLibraryFieldPayload) =>
    request<FieldDefinition>('/field-library', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateField: (id: string, payload: UpdateLibraryFieldPayload) =>
    request<FieldDefinition>(`/field-library/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  deleteField: (id: string) =>
    request<void>(`/field-library/${id}`, { method: 'DELETE' }),

  importFields: (rows: LibraryFieldImportRow[]) =>
    request<{ count: number; created: number; updated: number; fields: FieldDefinition[] }>('/field-library/import', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
}
